import React, { useState, useEffect, useRef } from 'react';
import { 
    TextField, 
    InputAdornment, 
    Paper, 
    List, 
    ListItem, 
    ListItemText, 
    CircularProgress,
    Box,
    Popper,
    ClickAwayListener
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { getAllProductsToSearch } from 'utils/LaravelApi/LaravelAPI';

const SearchField = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const timeoutRef = useRef(null);

    // Función para buscar en la API
    const searchItems = async (searchTerm) => {
        if (!searchTerm.trim()) {
            setResults([]);
            setOpen(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const results = await getAllProductsToSearch({ q: searchTerm });
            setResults(results);
            setOpen(true);
        } catch (err) {
            setError(err.message);
            setResults([]);
            setOpen(false);
        } finally {
            setLoading(false);
        }
    };

    // Debounce personalizado
    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            searchItems(query);
        }, 300);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [query]);

    // Manejar selección de resultado
    const handleResultSelect = (item) => {
        setQuery('');
        setOpen(false);
        // Redirigir al link del item dentro de la misma aplicación
        if (item.link) {
            // Forzar recarga completa para asegurar que el componente se actualice
            window.location.href = item.link;
        }
    };

    // Manejar el foco en el input
    const handleFocus = (event) => {
        setAnchorEl(event.currentTarget);
        if (query.trim() && results.length > 0) {
            setOpen(true);
        }
    };

    // Manejar click fuera
    const handleClickAway = () => {
        setOpen(false);
    };

    // Manejar cambio en el input
    const handleInputChange = (event) => {
        const value = event.target.value;
        setQuery(value);
        setAnchorEl(event.currentTarget);
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ position: 'relative', width: '100%' }}>
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={query}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder="Buscar productos..."
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'action.active', fontSize: '1.2rem' }} />
                            </InputAdornment>
                        ),
                        endAdornment: loading && (
                            <InputAdornment position="end">
                                <CircularProgress size={16} />
                            </InputAdornment>
                        ),
                        sx: {
                            backgroundColor: 'white',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: '1px solid rgba(0, 0, 0, 0.87)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: '2px solid #1976d2',
                            },
                            fontSize: '0.875rem',
                        }
                    }}
                    sx={{
                        '& .MuiInputBase-input': {
                            padding: '8px 0',
                        }
                    }}
                />

                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    style={{ zIndex: 1300, width: anchorEl ? anchorEl.offsetWidth : 'auto' }}
                    modifiers={[
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 4],
                            },
                        },
                    ]}
                >
                    <Paper 
                        elevation={8}
                        sx={{ 
                            maxHeight: 300, 
                            overflow: 'auto',
                            border: '1px solid rgba(0, 0, 0, 0.12)'
                        }}
                    >
                        {error ? (
                            <Box sx={{ p: 2, color: 'error.main', fontSize: '0.875rem' }}>
                                Error: {error}
                            </Box>
                        ) : results.length === 0 && query.trim() ? (
                            <Box sx={{ p: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                                No se encontraron resultados
                            </Box>
                        ) : (
                            <List dense>
                                {results.map((item) => (
                                    <ListItem 
                                        key={item.id}
                                        onClick={() => handleResultSelect(item)}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                            minHeight: 40,
                                        }}
                                    >
                                        <ListItemText 
                                            primary={item.titulo}
                                            sx={{
                                                '& .MuiTypography-root': {
                                                    fontSize: '0.875rem',
                                                    fontWeight: 400,
                                                }
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

export default SearchField;